import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { HomePageData } from '../../types/api';

export const HomePage: React.FC = () => {
    const [data, setData] = useState<HomePageData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        website_url_trap: '',
    });
    const [contactStatus, setContactStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        api.getHome().then(res => {
            if (res.data) setData(res.data);
        }).catch(() => {}).finally(() => {
            setLoading(false);
        });
    }, []);

    const handleContactSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setContactStatus(null);
        try {
            await api.submitContact(contactForm);
            setContactStatus({
                type: 'success',
                message: 'Your message has been sent successfully! Rakib will get back to you shortly.'
            });
            setContactForm({ name: '', email: '', subject: '', message: '', website_url_trap: '' });
        } catch (err: any) {
            setContactStatus({
                type: 'error',
                message: err.message || 'Failed to send message. Please verify your details and try again.'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const settings = data?.settings || {};
    const experiences = data?.experiences || [];
    const achievements = data?.achievements || [];
    const recentPosts = data?.recentPosts || [];

    const profilePhotoUrl = settings.profile_photo ? `/storage/${settings.profile_photo}` : "/rakib image.jpeg";
    const brandLogoUrl = settings.brand_logo ? `/storage/${settings.brand_logo}` : "/Rakib's logo.jpg";

    return (
        <div>
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center justify-center bg-radial-glow bg-grid-pattern pt-12 pb-20 overflow-hidden">
                {/* Ambient Blur Glows */}
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="absolute bottom-10 left-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                        
                        {/* Left Column: Copy & Actions */}
                        <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
                            {/* Badge */}
                            <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-slate-900 border border-emerald-500/30 text-emerald-400 text-xs font-semibold tracking-wider uppercase">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                {settings.hero_badge || 'Official Personal Brand'}
                            </div>

                            {/* Main Headline */}
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.12]">
                                Leading With <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-200">Purpose</span> & Civic Responsibility.
                            </h1>

                            {/* Subtitle / Persona */}
                            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
                                {settings.hero_subtitle || 'Official space of Rakib Hasan — Student Leader, Organizer & Youth Changemaker from Habiganj, Bangladesh. Fostering structured governance, team discipline, and community development.'}
                            </p>

                            {/* Action Buttons */}
                            <div className="pt-4 flex flex-wrap items-center justify-center lg:justify-start gap-4">
                                <a href="#leadership" className="px-6 py-3.5 rounded-full text-sm font-semibold tracking-wide bg-emerald-600 hover:bg-emerald-500 text-white shadow-xl shadow-emerald-950 hover:shadow-emerald-900/60 transition cursor-pointer">
                                    Explore Journey &rarr;
                                </a>
                                <Link to="/about" className="px-6 py-3.5 rounded-full text-sm font-semibold tracking-wide bg-slate-900/80 hover:bg-slate-800 text-slate-200 border border-slate-700 hover:border-slate-600 transition">
                                    About Rakib
                                </Link>
                                <a href="#contact" className="px-6 py-3.5 rounded-full text-sm font-semibold tracking-wide text-slate-400 hover:text-white transition">
                                    Get in Touch
                                </a>
                            </div>

                            {/* Verified Stats Bar */}
                            <div className="pt-8 border-t border-slate-800/80 grid grid-cols-3 gap-6 text-center lg:text-left">
                                <div>
                                    <div className="text-2xl sm:text-3xl font-extrabold text-white">6+</div>
                                    <div className="text-xs text-slate-400 tracking-wider uppercase mt-0.5">Leadership Roles</div>
                                </div>
                                <div>
                                    <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">100%</div>
                                    <div className="text-xs text-slate-400 tracking-wider uppercase mt-0.5">Verified Experience</div>
                                </div>
                                <div>
                                    <div className="text-2xl sm:text-3xl font-extrabold text-white">Habiganj</div>
                                    <div className="text-xs text-slate-400 tracking-wider uppercase mt-0.5">Sylhet, Bangladesh</div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Visual Portrait */}
                        <div className="lg:col-span-5 flex justify-center">
                            <div className="relative w-72 sm:w-80 lg:w-96">
                                {/* Glow Behind Image */}
                                <div className="absolute -inset-1.5 bg-gradient-to-tr from-emerald-500/40 via-teal-500/20 to-transparent rounded-3xl blur-xl opacity-70"></div>
                                
                                {/* Portrait Card */}
                                <div className="relative bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl p-2.5">
                                    <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden bg-slate-950">
                                        <img src={profilePhotoUrl} alt="Rakib Hasan" className="w-full h-full object-cover object-top hover:scale-105 transition duration-700" />
                                        
                                        {/* Bottom Overlay Card */}
                                        <div className="absolute bottom-3 inset-x-3 p-3.5 rounded-xl glass-dark border border-white/10 shadow-lg">
                                            <div className="flex items-center gap-3">
                                                <img src={brandLogoUrl} alt="Logo" className="w-10 h-10 rounded-full object-cover p-0.5 bg-slate-900 ring-1 ring-emerald-500" />
                                                <div>
                                                    <h2 className="text-sm font-bold text-white leading-tight">Rakib Hasan</h2>
                                                    <p className="text-xs text-emerald-400 font-medium">President • English Knowledge Holders</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* About Snippet & Philosophy Section */}
            <section id="about" className="py-24 bg-slate-900/60 border-t border-b border-slate-800/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        
                        <div className="lg:col-span-5 space-y-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold text-emerald-400 uppercase tracking-wider">
                                Core Philosophy
                            </div>
                            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white leading-snug">
                                "True leadership is rooted in disciplined service and organized civic impact."
                            </h2>
                            <p className="text-slate-400 text-base leading-relaxed">
                                {settings.bio_short || 'Rakib Hasan combines structured management with active community dedication. Serving across executive student bodies, charity coordination, and school ceremonial teams, he brings focused momentum to youth initiatives.'}
                            </p>
                            <div className="pt-2">
                                <Link to="/about" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition">
                                    Read Full Biography &rarr;
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="p-6 rounded-2xl glass-card glass-card-hover space-y-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                                </div>
                                <h3 className="text-lg font-bold text-white">Student Governance</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">Guiding youth assemblies, moderating educational workshops, and fostering confident peer communication.</p>
                            </div>

                            <div className="p-6 rounded-2xl glass-card glass-card-hover space-y-3">
                                <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <h3 className="text-lg font-bold text-white">Fiscal Stewardship</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">Overseeing honest, transparent treasuries for community campaigns in Interact Club and Muktanchol.</p>
                            </div>

                            <div className="p-6 rounded-2xl glass-card glass-card-hover space-y-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                                </div>
                                <h3 className="text-lg font-bold text-white">Cadence & Discipline</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">Instilling precision and teamwork as 1st Leader of the Habiganj Govt. High School Band Team.</p>
                            </div>

                            <div className="p-6 rounded-2xl glass-card glass-card-hover space-y-3">
                                <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                                </div>
                                <h3 className="text-lg font-bold text-white">Charity Partnerships</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">Facilitating youth welfare and benevolence drives with organizations like Magico'24.</p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* Leadership & Activities Section */}
            <section id="leadership" className="py-24 bg-slate-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-emerald-500/30 text-xs font-semibold text-emerald-400 uppercase tracking-wider">
                            Verified Records
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                            Leadership & Activities Journey
                        </h2>
                        <p className="text-base text-slate-400">
                            A verified record of executive responsibility, student leadership, and community service.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {experiences.length > 0 ? (
                            experiences.map(exp => (
                                <div key={exp.id} className="p-6 rounded-2xl glass-card glass-card-hover flex flex-col justify-between">
                                    <div className="space-y-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <span className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                    {exp.period || 'Active'}
                                                </span>
                                            </div>
                                            {exp.is_featured && (
                                                <span className="text-xs font-medium text-amber-400 flex items-center gap-1">
                                                    ★ Featured
                                                </span>
                                            )}
                                        </div>

                                        <div>
                                            <h3 className="text-xl font-bold text-white">{exp.role_title}</h3>
                                            <p className="text-sm font-medium text-emerald-400 mt-0.5">{exp.organization_name}</p>
                                        </div>

                                        <p className="text-sm text-slate-400 leading-relaxed">
                                            {exp.description}
                                        </p>
                                    </div>

                                    {exp.organization_url && (
                                        <div className="pt-4 border-t border-slate-800/80 mt-4">
                                            <a href={exp.organization_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-slate-400 hover:text-white transition flex items-center gap-1">
                                                Organization Website &rarr;
                                            </a>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="col-span-3 text-center py-12 text-slate-500">
                                {loading ? 'Loading leadership journey...' : 'No leadership records published yet.'}
                            </div>
                        )}
                    </div>

                    <div className="mt-12 text-center">
                        <Link to="/leadership" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 border border-slate-700 hover:border-slate-500 text-sm font-semibold text-slate-200 transition">
                            View Detailed Leadership Timeline &rarr;
                        </Link>
                    </div>

                </div>
            </section>

            {/* Achievements & Milestones Section */}
            <section className="py-24 bg-slate-900/40 border-t border-slate-800/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    <div className="text-center max-w-2xl mx-auto space-y-4 mb-16">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold text-emerald-400 uppercase tracking-wider">
                            Milestones
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                            Key Honors & Achievements
                        </h2>
                        <p className="text-sm text-slate-400">
                            Acknowledged contributions to student governance, financial integrity, and school ceremonies.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {achievements.map(ach => (
                            <div key={ach.id} className="p-6 rounded-2xl glass-card glass-card-hover space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold px-2.5 py-1 rounded bg-slate-800 text-slate-300 border border-slate-700">
                                        {ach.badge || 'Milestone'}
                                    </span>
                                    {ach.year && (
                                        <span className="text-xs text-slate-400 font-mono">{ach.year}</span>
                                    )}
                                </div>
                                <h3 className="text-lg font-bold text-white">{ach.title}</h3>
                                {ach.subtitle && (
                                    <p className="text-xs font-medium text-emerald-400">{ach.subtitle}</p>
                                )}
                                <p className="text-sm text-slate-400 leading-relaxed">{ach.description}</p>
                            </div>
                        ))}
                    </div>

                </div>
            </section>

            {/* Featured Blog Articles Section */}
            <section className="py-24 bg-slate-950 border-t border-slate-800/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                        <div>
                            <span className="text-xs font-bold text-emerald-400 tracking-wider uppercase">Perspectives</span>
                            <h2 className="text-3xl font-extrabold text-white tracking-tight mt-1">Articles & Insights</h2>
                        </div>
                        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition">
                            All Articles &rarr;
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {recentPosts.length > 0 ? (
                            recentPosts.map(post => (
                                <article key={post.id} className="p-6 rounded-2xl glass-card glass-card-hover flex flex-col justify-between">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between text-xs text-slate-400">
                                            <span className="font-medium text-emerald-400">{post.category?.name || 'General'}</span>
                                            <span>{post.reading_time_minutes} min read</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-white leading-snug">
                                            <Link to={`/blog/${post.slug}`} className="hover:text-emerald-400 transition">
                                                {post.title}
                                            </Link>
                                        </h3>
                                        <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">
                                            {post.excerpt}
                                        </p>
                                    </div>

                                    <div className="pt-6 border-t border-slate-800/80 mt-6 flex items-center justify-between text-xs text-slate-400">
                                        <span>{post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
                                        <Link to={`/blog/${post.slug}`} className="font-semibold text-emerald-400 hover:text-emerald-300 transition">
                                            Read Article &rarr;
                                        </Link>
                                    </div>
                                </article>
                            ))
                        ) : (
                            <div className="col-span-3 text-center py-12 text-slate-500">
                                No articles published yet.
                            </div>
                        )}
                    </div>

                </div>
            </section>

            {/* Contact Form Section */}
            <section id="contact" className="py-24 bg-slate-900/60 border-t border-slate-800/80">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    <div className="text-center space-y-4 mb-12">
                        <span className="text-xs font-bold text-emerald-400 tracking-wider uppercase">Direct Connection</span>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Connect With Rakib</h2>
                        <p className="text-sm text-slate-400 max-w-xl mx-auto">
                            Reach out for student leadership workshops, event partnerships, youth initiatives, or inquiries.
                        </p>
                    </div>

                    <div className="p-8 sm:p-10 rounded-3xl glass-card shadow-2xl">
                        {contactStatus && (
                            <div className={`mb-6 p-4 rounded-xl text-sm font-medium ${contactStatus.type === 'success' ? 'bg-emerald-900/80 text-emerald-200 border border-emerald-500/40' : 'bg-rose-900/80 text-rose-200 border border-rose-500/40'}`}>
                                {contactStatus.message}
                            </div>
                        )}

                        <form onSubmit={handleContactSubmit} className="space-y-6">
                            {/* Spam Honeypot */}
                            <input 
                                type="text" 
                                name="website_url_trap" 
                                value={contactForm.website_url_trap} 
                                onChange={e => setContactForm({ ...contactForm, website_url_trap: e.target.value })} 
                                className="hidden" 
                                tabIndex={-1} 
                                autoComplete="off" 
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="home-name" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Your Name</label>
                                    <input 
                                        type="text" 
                                        id="home-name" 
                                        required 
                                        value={contactForm.name}
                                        onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm" 
                                    />
                                </div>

                                <div>
                                    <label htmlFor="home-email" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Your Email</label>
                                    <input 
                                        type="email" 
                                        id="home-email" 
                                        required 
                                        value={contactForm.email}
                                        onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm" 
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="home-subject" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Subject</label>
                                <input 
                                    type="text" 
                                    id="home-subject" 
                                    required 
                                    value={contactForm.subject}
                                    onChange={e => setContactForm({ ...contactForm, subject: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm" 
                                />
                            </div>

                            <div>
                                <label htmlFor="home-message" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Message</label>
                                <textarea 
                                    id="home-message" 
                                    rows={5} 
                                    required 
                                    value={contactForm.message}
                                    onChange={e => setContactForm({ ...contactForm, message: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm" 
                                />
                            </div>

                            <div className="text-right">
                                <button 
                                    type="submit" 
                                    disabled={submitting}
                                    className="px-8 py-3.5 rounded-full text-sm font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-950 hover:shadow-emerald-900 transition cursor-pointer disabled:opacity-50"
                                >
                                    {submitting ? 'Sending...' : 'Send Message →'}
                                </button>
                            </div>
                        </form>
                    </div>

                </div>
            </section>
        </div>
    );
};
